// src/components/manuscripts/ManuscriptDetail.jsx
//
// Where a manuscript card goes when you click it, per the prototype. This is a
// view swap inside the Manuscripts tab, not a route -- the prototype's "Back to
// Manuscripts" link returns to the same grid with its scroll position intact.
//
// What is wired to real data and what is not is annotated inline and summarised
// in the ticket handover. The short version: the hero, synopsis, genre,
// milestones, due date and notes are live; Chapters and Activity are empty
// states because the prototype shows the tabs without saying what is in them.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, CircularProgress, Menu, TextField, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CreateIcon from '@mui/icons-material/Create';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { tokens } from '../../theme';
import { formatDate } from '../../utils/editorUtils';
import { getJSON, postJSON } from '../../utils/api';
import { manuscriptMenuItems, statusTagFor, wordCountLabel } from './ManuscriptCard';
import { QUIET_BUTTON } from './manuscriptStyles';
import '../../styles/ManuscriptWorkspace.css';

const TABS = ['Overview', 'Chapters', 'Notes', 'Activity'];

// Edit and View are parked: hidden "for now" on the scrum master's say-so, with
// their behaviour still undecided. The code stays so it is a one-flag change to
// bring them back -- it is not dead code to clean up.
const SHOW_PARKED_ACTIONS = false;

/** Tasks done / tasks planned for one milestone, ignoring the dropped ones. */
export function milestoneProgress(milestone) {
  const tasks = (milestone?.tasks || []).filter((t) => {
    const status = t.status || (t.completed ? 'completed' : 'unstarted');
    return status !== 'cancelled' && status !== 'rejected';
  });
  if (!tasks.length) return null;
  const done = tasks.filter(
    (t) => (t.status || (t.completed ? 'completed' : 'unstarted')) === 'completed'
  ).length;
  return { done, total: tasks.length, percent: Math.round((done * 100) / tasks.length) };
}

/**
 * The date the sidebar shows.
 *
 * The prototype has a "Due Date" field and nothing on a draft carries one. Two
 * real sources do: the next unfinished milestone in the manuscript's own plan,
 * and -- when you are a collaborator -- the due date on the invitation that put
 * you on it. The plan wins when both exist, because it is the manuscript's own
 * deadline rather than yours.
 */
export function nextDueDate(milestones, invitation) {
  const pending = (milestones || [])
    .filter((ms) => {
      const p = milestoneProgress(ms);
      return ms.due_date && (!p || p.percent < 100);
    })
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  if (pending.length) return pending[0].due_date;
  return invitation?.dueDate || null;
}

export default function ManuscriptDetail({
  manuscript,
  isCollaboration = false,
  onBack,
  onOpenEditor,
  onDelete,
  onUnassign,
  onOpenStructure,
  onOpenPricing,
  onLinkCover,
}) {
  const [tab, setTab] = useState('Overview');
  const [moreAnchor, setMoreAnchor] = useState(null);
  const [coverFailed, setCoverFailed] = useState(false);

  const [milestones, setMilestones] = useState([]);
  const [goalsLoading, setGoalsLoading] = useState(true);

  // The accepted invitation that put me on this manuscript, if I am a
  // collaborator. `null` while it is being looked up, which is NOT the same as
  // "there isn't one" -- the Unassign button distinguishes the two rather than
  // being absent for both, which is how its absence went unnoticed before.
  const [invitation, setInvitation] = useState(null);
  const [invitationChecked, setInvitationChecked] = useState(false);

  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [draftNote, setDraftNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [noteError, setNoteError] = useState('');

  const id = manuscript._id;

  const hasMore = Boolean(onOpenStructure || onOpenPricing || onLinkCover);
  const closeMore = () => setMoreAnchor(null);
  const runAndClose = (fn) => () => {
    closeMore();
    fn?.(id);
  };

  useEffect(() => {
    setTab('Overview');
    setCoverFailed(false);
  }, [id]);

  useEffect(() => {
    let alive = true;
    setGoalsLoading(true);
    (async () => {
      try {
        const data = await getJSON(`/api/drafts/${id}/goals`);
        if (alive) setMilestones(data?.milestones || []);
      } catch {
        // A manuscript with no plan 404s or 403s depending on who is asking;
        // either way the Milestones pane shows its empty state rather than an
        // error, because "no milestones" is the normal case.
        if (alive) setMilestones([]);
      } finally {
        if (alive) setGoalsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  useEffect(() => {
    if (!isCollaboration) {
      setInvitation(null);
      setInvitationChecked(true);
      return undefined;
    }
    let alive = true;
    setInvitationChecked(false);
    (async () => {
      let found = null;
      try {
        const list = await getJSON('/api/invitations/incoming?status=accepted');
        if (Array.isArray(list)) {
          found = list.find(
            (inv) => inv.type === 'draft' && String(inv.draftId) === String(id)
          ) || null;
        }
      } catch {
        found = null;
      }
      if (!alive) return;
      setInvitation(found);
      setInvitationChecked(true);
    })();
    return () => { alive = false; };
  }, [id, isCollaboration]);

  const loadNotes = useCallback(async () => {
    setNotesLoading(true);
    try {
      const data = await getJSON(`/api/books/${id}/note-items`);
      setNotes(data?.notes || []);
    } catch {
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  }, [id]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const addNote = async () => {
    const text = draftNote.trim();
    if (!text) return;
    setAddingNote(true);
    setNoteError('');
    try {
      await postJSON(`/api/books/${id}/note-items`, { text });
      setDraftNote('');
      await loadNotes();
    } catch (e) {
      setNoteError('Could not save that note. Try again.');
    } finally {
      setAddingNote(false);
    }
  };

  const tag = statusTagFor(manuscript, { isCollaboration });
  const coverUrl = !coverFailed ? manuscript.cover_url || '' : '';
  const progress = manuscript.taskProgress || null;
  const due = nextDueDate(milestones, invitation);

  // One `genre` string on the draft; the prototype shows several chips, so a
  // comma- or slash-separated value splits into one chip each.
  const genres = useMemo(
    () => (manuscript.genre || '').split(/[,/]/).map((s) => s.trim()).filter(Boolean),
    [manuscript.genre]
  );

  return (
    <div className="rv-manuscript-detail" data-testid="manuscript-detail">
      <Button
        onClick={onBack}
        data-testid="manuscript-detail-back"
        startIcon={<ArrowBackIcon />}
        sx={{ ...QUIET_BUTTON, alignSelf: 'flex-start', minHeight: 'auto', px: 0, fontWeight: 700 }}
      >
        Back to Manuscripts
      </Button>

      <div className="rv-manuscript-detail__hero">
        {coverUrl ? (
          <img
            className="rv-manuscript-detail__cover"
            src={coverUrl}
            alt={`Cover of ${manuscript.title || 'Untitled'}`}
            onError={() => setCoverFailed(true)}
          />
        ) : (
          <div
            className="rv-manuscript-detail__cover rv-manuscript-detail__cover--empty"
            aria-hidden="true"
          >
            <MenuBookIcon />
          </div>
        )}

        <div className="rv-manuscript-detail__meta">
          <h2 className="rv-manuscript-detail__title">{manuscript.title || 'Untitled'}</h2>

          <div className="rv-manuscript-detail__submeta">
            <span className={`rv-status-tag rv-status-tag--${tag.modifier}`}>{tag.label}</span>
            <span>Last edited: {formatDate(manuscript.lastEdited)}</span>
            <span>&bull;</span>
            <span>{wordCountLabel(manuscript)}</span>
          </div>

          <div className="rv-manuscript-detail__actions">
            <Button
              variant="contained"
              size="small"
              sx={QUIET_BUTTON}
              data-testid="manuscript-continue-writing"
              startIcon={<CreateIcon />}
              onClick={() => onOpenEditor?.(id)}
            >
              Continue Writing
            </Button>
            {/* PARKED, NOT DEAD: Edit and View are hidden "for now" per the
                scrum master's feedback. Do not delete this block -- flip
                SHOW_PARKED_ACTIONS (top of file) once someone says what they
                should do and wire them up here.

                Until then they were rendered disabled, on purpose.

                The prototype draws five buttons here and says what none of
                them do. "Continue Writing" is unambiguous -- there is one
                editor and that is where writing continues. The other two are
                not: "Edit" sat beside "Continue Writing" with nothing to
                distinguish them, and "View" could be the reader, a preview, a
                public page. Earlier revisions of this file pointed Edit at
                /manuscript/:id/structure and View at the draft PDF; both were
                this session's inventions rather than anything the ticket
                asked for, and View additionally 500s on any manuscript that
                has a cover.

                Keeping them visible and inert keeps the prototype's layout
                honest -- the buttons ARE in the design -- while making it
                obvious that the behaviour behind them is still an open
                question rather than something already decided. Wiring each
                one up is a line, once someone says where it should go. */}
            {SHOW_PARKED_ACTIONS && [
              {
                label: 'Edit',
                icon: <EditIcon />,
                why: 'Not wired up yet — the prototype shows Edit and Continue Writing without saying how they differ.',
              },
              {
                label: 'View',
                icon: <VisibilityIcon />,
                why: 'Not wired up yet — tell us whether this should open the reader, a preview, or something else.',
              },
            ].map(({ label, icon, why }) => (
              <Tooltip key={label} title={why}>
                {/* `span` because a disabled MUI button swallows the events
                    Tooltip listens for. */}
                <span>
                  <Button variant="outlined" size="small" sx={QUIET_BUTTON} disabled startIcon={icon}>
                    {label}
                  </Button>
                </span>
              </Tooltip>
            ))}

            {/* The prototype's danger slot says "Unassign", which only means
                anything to a collaborator -- and only works through the
                invitation that put them on the manuscript, which is why it is
                absent when we could not match one. On your own manuscript the
                destructive action in this tab has always been Delete, and the
                two are emphatically not the same thing, so the slot switches
                label rather than pretending. */}
            {isCollaboration
              ? invitationChecked && (
                  <Tooltip
                    title={
                      invitation
                        ? ''
                        : 'Only the author can remove you from this manuscript — no invitation of yours is on it.'
                    }
                  >
                    {/* `span` because a disabled MUI button swallows the
                        events Tooltip listens for. */}
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        sx={QUIET_BUTTON}
                        disabled={!invitation}
                        startIcon={<PersonRemoveIcon />}
                        onClick={() => onUnassign?.(invitation)}
                      >
                        Unassign
                      </Button>
                    </span>
                  </Tooltip>
                )
              : onDelete && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    sx={QUIET_BUTTON}
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => onDelete?.(id)}
                  >
                    Delete
                  </Button>
                )}

            {/* The prototype's More carries no items; the scrum master's
                feedback settled it: the same three as the card's overflow menu
                (Manuscript Structure, Book Pricing Estimator, Link Cover).
                The items come from the card's own `manuscriptMenuItems`, so
                labels and the Link/Change Cover wording cannot drift. Unlink
                Cover and Delete are deliberately not passed -- Delete already
                has its own button in this row, and the feedback named three.
                Which items appear follows which handlers the workspace gave
                us (pricing and cover link are owner-only), and with none the
                button is not rendered at all. */}
            {hasMore && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  sx={QUIET_BUTTON}
                  data-testid="manuscript-detail-more"
                  aria-haspopup="menu"
                  aria-expanded={Boolean(moreAnchor)}
                  endIcon={<ExpandMoreIcon />}
                  onClick={(e) => setMoreAnchor(e.currentTarget)}
                >
                  More
                </Button>
                <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={closeMore}>
                  {manuscriptMenuItems(
                    manuscript,
                    { onOpenStructure, onOpenPricing, onLinkCover },
                    runAndClose
                  )}
                </Menu>
              </>
            )}
          </div>
        </div>
      </div>

      {/* MUI Tabs would bring its own indicator, ripple and type scale; the
          prototype's row is a plain underline, so these are buttons styled by
          the same tokens as everything else here. */}
      <div
        role="tablist"
        aria-label="Manuscript detail"
        style={{
          display: 'flex',
          gap: tokens.space.xl,
          borderBottom: `1px solid ${tokens.color.line}`,
        }}
      >
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: `2px solid ${tab === t ? tokens.color.purple : 'transparent'}`,
              marginBottom: -1,
              padding: `0 0 ${tokens.space.sm}px`,
              cursor: 'pointer',
              fontFamily: tokens.family.sans,
              fontSize: tokens.text.label.size,
              fontWeight: tab === t ? 700 : 500,
              color: tab === t ? tokens.color.purple : tokens.color.muted,
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Overview' && (
        <div className="rv-manuscript-detail__panes">
          <div className="rv-manuscript-detail__left">
            <section className="rv-pane">
              <h3 className="rv-pane__title">Synopsis</h3>
              <p className={`rv-pane__text${manuscript.blurb ? '' : ' rv-pane__text--muted'}`}>
                {manuscript.blurb || 'No synopsis yet. Add one from the blurb editor.'}
              </p>
              <h3 className="rv-pane__title">Genre</h3>
              {genres.length ? (
                <div className="rv-pane__tags">
                  {genres.map((gname) => (
                    <span key={gname} className="rv-pane__tag">{gname}</span>
                  ))}
                </div>
              ) : (
                <p className="rv-pane__text rv-pane__text--muted" style={{ margin: 0 }}>
                  No genre set.
                </p>
              )}
            </section>

            <section className="rv-pane">
              <h3 className="rv-pane__title">Milestones</h3>
              {goalsLoading ? (
                <CircularProgress size={20} />
              ) : milestones.length === 0 ? (
                <p className="rv-pane__text rv-pane__text--muted" style={{ margin: 0 }}>
                  No milestones yet. Set them up from the editor&rsquo;s Goals panel.
                </p>
              ) : (
                milestones.map((ms, i) => {
                  const p = milestoneProgress(ms);
                  const done = p?.percent === 100;
                  const notStarted = !p || p.percent === 0;
                  return (
                    <div
                      key={ms.name ? `${ms.name}-${i}` : i}
                      className={`rv-milestone${notStarted ? ' rv-milestone--todo' : ''}`}
                    >
                      <div className="rv-milestone__left">
                        {done ? (
                          <CheckCircleIcon
                            fontSize="small"
                            sx={{ color: 'success.main', flex: '0 0 auto' }}
                          />
                        ) : (
                          <RadioButtonUncheckedIcon
                            fontSize="small"
                            sx={{ color: 'text.disabled', flex: '0 0 auto' }}
                          />
                        )}
                        <span>{ms.name || `Milestone ${i + 1}`}</span>
                      </div>

                      {done || notStarted ? (
                        <span className="rv-milestone__status">
                          {done ? 'Completed' : 'Not started'}
                        </span>
                      ) : (
                        <div className="rv-milestone__progress">
                          <div
                            className="rv-progress-track"
                            role="progressbar"
                            aria-label={`${ms.name || 'Milestone'} progress`}
                            aria-valuenow={p.percent}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          >
                            <div
                              className="rv-progress-track__fill"
                              style={{ width: `${p.percent}%` }}
                            />
                          </div>
                          <span className="rv-milestone__percent">{p.percent}%</span>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </section>
          </div>

          <aside className="rv-manuscript-detail__right">
            <div className="rv-meta-block">
              <span className="rv-meta-block__label">Due Date</span>
              <span className="rv-meta-block__value">
                {due ? formatDate(due) : '—'}
              </span>
            </div>

            {/* The prototype reads "4,320 / 13,500" with the circle showing the
                ratio. There is no per-manuscript word target in this model, so
                the figure is the count alone and the circle carries the task
                progress the cards already show. */}
            <div className="rv-manuscript-detail__progress-row">
              <div className="rv-meta-block">
                <span className="rv-meta-block__label">Word Count</span>
                <span className="rv-meta-block__value">
                  {typeof manuscript.wordCount === 'number'
                    ? manuscript.wordCount.toLocaleString()
                    : '—'}
                </span>
              </div>
              {/* No ring when there is no plan. An empty ring with a dash
                  in it reads as a widget that failed to load, rather than as
                  a number that does not exist yet. */}
              {progress && (
                <div className="rv-circle-badge">{`${progress.percent}%`}</div>
              )}
            </div>

            <div className="rv-manuscript-detail__notes-title">Notes</div>
            {notesLoading ? (
              <CircularProgress size={20} />
            ) : notes.length === 0 ? (
              <span className="rv-calendar__agenda-empty">No notes on this manuscript yet.</span>
            ) : (
              notes.slice(0, 5).map((n) => (
                <div key={n.id} className="rv-note">
                  <div className="rv-note__body">
                    {n.text}
                    <span className="rv-note__date">{formatDate(n.createdAt)}</span>
                  </div>
                </div>
              ))
            )}

            <TextField
              size="small"
              multiline
              minRows={2}
              placeholder="Add a note…"
              value={draftNote}
              onChange={(e) => setDraftNote(e.target.value)}
              error={Boolean(noteError)}
              helperText={noteError || ' '}
              inputProps={{ 'aria-label': 'Add a note' }}
            />
            <Button
              variant="outlined"
              size="small"
              sx={QUIET_BUTTON}
              disabled={addingNote || !draftNote.trim()}
              onClick={addNote}
            >
              {addingNote ? 'Saving…' : '+ Add note'}
            </Button>
          </aside>
        </div>
      )}

      {tab === 'Notes' && (
        <div>
          {notesLoading ? (
            <CircularProgress size={20} />
          ) : notes.length === 0 ? (
            <p className="rv-manuscript-detail__placeholder">
              No notes on this manuscript yet. Add one from the Overview tab.
            </p>
          ) : (
            <div style={{ display: 'grid', gap: tokens.space.sm }}>
              {notes.map((n) => (
                <div key={n.id} className="rv-note">
                  <div className="rv-note__body">
                    {n.text}
                    <span className="rv-note__date">{formatDate(n.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* The prototype shows these two tabs and no content for either, and
          nothing in the app answers what they would hold -- `manuscriptStructure`
          is front and back matter rather than chapters, and there is no activity
          log on a draft. Raised in the handover; they say what they are rather
          than guessing. */}
      {tab === 'Chapters' && (
        <p className="rv-manuscript-detail__placeholder">
          Chapters aren&rsquo;t available here yet. Open the manuscript in the editor to work
          through it.
        </p>
      )}

      {tab === 'Activity' && (
        <p className="rv-manuscript-detail__placeholder">
          Activity isn&rsquo;t available here yet.
        </p>
      )}
    </div>
  );
}
